import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export const MAX_ACCESS_ATTEMPTS = 3;

// routes whose API entity is named differently (read from src/lib/data)
const ROUTE_RESOURCE_ALIASES: Record<string, string> = {
  claims: 'claim',
  jobs: 'glazier-jobs',
  'fmi-enquiries': 'enquires',
  customers: 'catalog/customer',
  fabricators: 'catalog/fabricator',
  glaziers: 'catalog/glazier',
  'fmi-users': 'catalog/user',
  'fabricator-members': 'fabricator',
};

/** First path segment, or `catalog/<second>` for the catalog entities. */
const getUrlResourceKey = (url: string) => {
  const [first = '', second = ''] = url
    .split('?')[0]
    .split('/')
    .filter(Boolean);

  return first === 'catalog' ? `catalog/${second}` : first;
};

type AccessDeniedPhase = 'idle' | 'updating' | 'denied';

interface AccessDeniedState {
  /** pathname the denial was reported on */
  path: string | null;
  resource: string | null;
  url: string | null;
  /** token renewals tried for this page, kept across a successful one */
  attempts: number;
  phase: AccessDeniedPhase;
  /** pathname of the mounted PermissionGuard, the only place a denial can render; kept by reset() */
  guardedPath: string | null;
  /** true when `url` is the current page's own resource on a guarded route; the denial is then held */
  reportForbidden: (url: string) => boolean;
  setGuardedPath: (path: string) => void;
  /** clears the guarded path only while it still is `path` */
  releaseGuardedPath: (path: string) => void;
  attemptFailed: () => void;
  attemptSucceeded: () => void;
  reset: () => void;
}

const initialState = {
  path: null,
  resource: null,
  url: null,
  attempts: 0,
  phase: 'idle' as AccessDeniedPhase,
};

export const useAccessDeniedStore = create<AccessDeniedState>()(
  devtools(
    (set, get) => ({
      ...initialState,
      guardedPath: null,
      reportForbidden: (url) => {
        const path = window.location.pathname;

        // no guard on this route: nothing would render the denial
        if (get().guardedPath !== path) return false;

        const resource = path.split('/').filter(Boolean)[0] || '';
        const key = getUrlResourceKey(url);

        if (!resource || !key) return false;
        if (key !== resource && key !== ROUTE_RESOURCE_ALIASES[resource]) {
          return false;
        }

        // the counter belongs to the page, not to the mount
        const attempts = get().path === path ? get().attempts : 0;

        set(
          {
            path,
            resource,
            url,
            attempts,
            phase: attempts >= MAX_ACCESS_ATTEMPTS ? 'denied' : 'updating',
          },
          false,
          'reportForbidden',
        );

        return true;
      },
      setGuardedPath: (path) =>
        set({ guardedPath: path }, false, 'setGuardedPath'),
      releaseGuardedPath: (path) => {
        if (get().guardedPath !== path) return;

        set({ guardedPath: null }, false, 'releaseGuardedPath');
      },
      attemptFailed: () =>
        set(
          (state) => {
            const attempts = state.attempts + 1;

            return {
              attempts,
              phase: attempts >= MAX_ACCESS_ATTEMPTS ? 'denied' : 'updating',
            };
          },
          false,
          'attemptFailed',
        ),
      attemptSucceeded: () =>
        set(
          (state) => ({ attempts: state.attempts + 1, phase: 'idle' }),
          false,
          'attemptSucceeded',
        ),
      reset: () => set({ ...initialState }, false, 'reset'),
    }),
    { name: 'access-denied-store' },
  ),
);
